import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/lib/useIsMobile';
import { notesApi } from '@/api/notes';
import { errorMessage } from '@/api/client';
import { tagsApi } from '@/api/tags';
import TagPill from '@/components/ui/TagPill';
import { useAuthStore } from '@/stores/auth';
import NoteEditor from '@/features/notes/NoteEditor';
import TemplatePickerModal from '@/features/notes/TemplatePickerModal';
import { exportNotesToZip, triggerDownload } from '@/features/notes/exportMarkdown';
import { importNotesFromZip, type ImportResult } from '@/features/notes/importMarkdown';
import { ChevronLeftIcon, StarIcon } from '@/features/notes/icons';
import { indexExistingDrafts } from '@/features/notes/noteDraft';
import { useDraftsStore } from '@/stores/drafts';
import { alertDialog } from '@/stores/dialog';
import { buildTree, TreeItem } from '@/features/notes/NoteTree';

// Culori rotative pentru root folders în tree — paletă codex multi-color.
const NOTE_ROOT_COLORS = [
  'var(--c-mint)',
  'var(--c-cyan)',
  'var(--c-purple)',
  'var(--c-amber)',
  'var(--c-rose)',
  'var(--c-blue)',
  'var(--c-indigo)',
];

export default function NotesList() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const user = useAuthStore((s) => s.user);
  const keyPair = useAuthStore((s) => s.keyPair);
  const currentTenant = user?.tenants.find((t) => t.tenant_id === tenantId);
  const tenantSlug = currentTenant?.slug || '';
  const [exporting, setExporting] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const navigate = useNavigate();
  const params = useParams<{ tenantSlug?: string; '*'?: string }>();
  const urlPath = params['*'] ?? '';

  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creatingUnder, setCreatingUnder] = useState<string | null | 'root'>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null | 'root'>(null);
  const [templatePickerFor, setTemplatePickerFor] = useState<string | null | 'root' | null>(null);
  const [importing, setImporting] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const list = useQuery({
    queryKey: ['notes', tenantId],
    queryFn: () => notesApi.list(tenantId!),
    enabled: !!tenantId,
  });

  // URL → selected ID. Rezolvă path-ul din URL la o notiță din listă.
  useEffect(() => {
    if (!list.data || !urlPath) return;
    const segments = urlPath.split('/').filter(Boolean);
    if (segments.length === 0) return;
    // Traversăm arborele greedy prin slug-urile din URL.
    let parentId: string | null = null;
    let matchId: string | null = null;
    for (const seg of segments) {
      const match = list.data.find(
        (n) => n.slug === seg && (n.parent_id ?? null) === parentId,
      );
      if (!match) {
        matchId = null;
        break;
      }
      matchId = match.id;
      parentId = match.id;
    }
    if (matchId && matchId !== selectedId) {
      setSelectedId(matchId);
      setCreatingUnder(null);
    }
  }, [list.data, urlPath, selectedId]);

  // Helper pentru a construi URL-ul unei notițe pe baza lanțului de slug-uri.
  const buildUrlForNote = (noteId: string): string => {
    if (!list.data || !tenantSlug) return '/notes';
    const byId = new Map(list.data.map((n) => [n.id, n]));
    const segments: string[] = [];
    let current = byId.get(noteId);
    const seen = new Set<string>();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      segments.unshift(current.slug);
      if (!current.parent_id) break;
      current = byId.get(current.parent_id);
    }
    return `/notes/${tenantSlug}/${segments.join('/')}`;
  };

  const selectNote = (id: string) => {
    setSelectedId(id);
    setCreatingUnder(null);
    const url = buildUrlForNote(id);
    navigate(url, { replace: false });
  };

  const moveMutation = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      notesApi.move(id, parentId),
    onSuccess: () => {
      setMoveError(null);
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
    },
    onError: (err) => {
      setMoveError(errorMessage(err));
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      notesApi.rename(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
    },
    onError: (err) => {
      setMoveError(errorMessage(err));
    },
  });

  const pinMutation = useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: boolean }) =>
      pin ? notesApi.pin(id) : notesApi.unpin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
    },
  });

  const pinnedNotes = useMemo(
    () => (list.data ?? []).filter((n) => n.pinned),
    [list.data],
  );

  // Filtru după tag: doar tag-urile puse pe cel puțin o notiță vizibilă.
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const tagsQuery = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId!),
    enabled: !!tenantId,
  });
  const usedTags = useMemo(() => {
    const used = new Set((list.data ?? []).flatMap((n) => n.tag_ids));
    return (tagsQuery.data ?? []).filter((tag) => used.has(tag.id));
  }, [list.data, tagsQuery.data]);
  const taggedNotes = useMemo(
    () => (tagFilter ? (list.data ?? []).filter((n) => n.tag_ids.includes(tagFilter)) : []),
    [list.data, tagFilter],
  );
  // Tag-ul filtrat a dispărut (șters sau scos de pe ultima notiță) → fără filtru.
  useEffect(() => {
    if (tagFilter && !usedTags.some((tag) => tag.id === tagFilter)) setTagFilter(null);
  }, [tagFilter, usedTags]);

  const draftIds = useDraftsStore((s) => s.withDraft);

  // Populează store-ul cu draft-uri existente (scan localStorage o dată per
  // schimbare de user).
  useEffect(() => {
    if (user?.id) indexExistingDrafts(user.id);
  }, [user?.id]);

  const handleDropOn = (targetId: string | null) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetId(null);
    const draggedId = e.dataTransfer.getData('application/cerbes-note');
    if (!draggedId) return;
    if (draggedId === targetId) return;
    moveMutation.mutate({ id: draggedId, parentId: targetId });
  };

  const tree = useMemo(() => buildTree(list.data ?? []), [list.data]);

  // Declarat înainte de early return-ul de mai jos (rules of hooks).
  const touchRef = useRef<{ x: number; y: number; t: number } | null>(null);

  if (!tenantId) {
    return <p className="text-sm text-[var(--text-dim)]">{t('notes_ui.list.no_tenant')}</p>;
  }

  const isCreating = creatingUnder !== null;

  // Master-detail mobile: sub md arătăm fie LISTA, fie DETALIUL, nu ambele.
  const showDetail = selectedId !== null || isCreating;
  const showListPanel = !isMobile || !showDetail;
  const showDetailPanel = !isMobile || showDetail;

  // Swipe-right pe detaliu → back la listă. Verticalul > orizontal => scroll,
  // nu swipe.
  const onTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const tch = e.touches[0];
    touchRef.current = { x: tch.clientX, y: tch.clientY, t: Date.now() };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || !touchRef.current) return;
    const tch = e.changedTouches[0];
    const dx = tch.clientX - touchRef.current.x;
    const dy = tch.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    touchRef.current = null;
    // threshold: minim 70px orizontal, mai orizontal decât vertical, sub 600ms
    if (dx > 70 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 600) {
      setSelectedId(null);
      setCreatingUnder(null);
      if (tenantSlug) navigate(`/notes/${tenantSlug}`, { replace: false });
      else navigate('/notes', { replace: false });
    }
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar notițe */}
      <aside
        className={`${showListPanel ? 'flex' : 'hidden'} ${
          isMobile ? 'w-full' : 'w-72 flex-shrink-0'
        } bg-[var(--bg-elevated)] ${
          isMobile ? '' : 'border-r border-[var(--border)]'
        } flex-col`}
      >
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="section-label no-dot !mb-0">{t('notes.title')}</h2>
          <div className="flex items-center gap-3 text-xs">
            {keyPair && currentTenant && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setImporting(t('notes_ui.list.import_preparing'));
                    try {
                      const result = await importNotesFromZip(f, {
                        tenantId,
                        keyPair,
                        onProgress: (p) =>
                          setImporting(`${p.done}/${p.total} — ${p.currentLabel}`),
                      });
                      setImportResult(result);
                      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
                    } catch (err) {
                      void alertDialog(err instanceof Error ? err.message : t('notes_ui.list.import_error'));
                    } finally {
                      setImporting(null);
                      if (importInputRef.current) importInputRef.current.value = '';
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => importInputRef.current?.click()}
                  disabled={!!importing}
                  className="text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300 disabled:opacity-50"
                  title={t('notes.import_tooltip')}
                >
                  {importing ? '...' : t('notes.import')}
                </button>
              </>
            )}
            {list.data && list.data.length > 0 && keyPair && currentTenant && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const blob = await exportNotesToZip({
                      tenantName: currentTenant.name,
                      notes: list.data!,
                      keyPair,
                      onProgress: (p) =>
                        setExporting(`${p.done}/${p.total} — ${p.currentLabel}`),
                    });
                    triggerDownload(
                      blob,
                      `cerbes-${tenantSlug}-${new Date().toISOString().slice(0, 10)}.zip`,
                    );
                  } catch (e) {
                    void alertDialog(e instanceof Error ? e.message : t('notes_ui.list.export_error'));
                  } finally {
                    setExporting(null);
                  }
                }}
                disabled={!!exporting}
                className="hover:underline disabled:opacity-50"
                style={{ color: 'var(--text-muted)' }}
                title={t('notes.export_tooltip')}
              >
                {exporting ? '...' : t('notes.export')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setTemplatePickerFor('root')}
              className="hover:underline"
              style={{ color: 'var(--text-muted)' }}
              title={t('notes.from_template_tooltip')}
            >
              {t('notes.from_template')}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreatingUnder('root');
                setSelectedId(null);
              }}
              className="hover:underline font-medium"
              style={{ color: 'var(--accent)' }}
              title={t('notes.new_root_tooltip')}
            >
              {t('notes.new_note')}
            </button>
          </div>
        </div>
        {exporting && (
          <div className="text-[10px] text-[var(--text-muted)] px-4 py-1 border-b border-[var(--border)] truncate">
            {t('notes.export')}: {exporting}
          </div>
        )}
        {importing && (
          <div className="text-[10px] text-[var(--text-muted)] px-4 py-1 border-b border-[var(--border)] truncate">
            {t('notes.import')}: {importing}
          </div>
        )}
        {importResult && (
          <div className="text-[10px] px-4 py-2 border-b border-[var(--border)]">
            <div style={{ color: 'var(--accent)' }}>
              {t('notes_ui.list.import_created', { count: importResult.created })}
              {importResult.failed.length > 0 && (
                <> · <span style={{ color: 'var(--danger)' }}>{t('notes_ui.list.import_errors', { count: importResult.failed.length })}</span></>
              )}
            </div>
            {importResult.failed.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer" style={{ color: 'var(--text-muted)' }}>
                  {t('notes_ui.list.import_error_details')}
                </summary>
                <ul className="mt-1 space-y-0.5 font-mono">
                  {importResult.failed.slice(0, 10).map((f, i) => (
                    <li key={i} style={{ color: 'var(--danger)' }}>
                      {f.path}: {f.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              className="mt-1 hover:underline"
              style={{ color: 'var(--text-dim)' }}
              onClick={() => setImportResult(null)}
            >
              {t('notes_ui.list.import_dismiss')}
            </button>
          </div>
        )}
        {moveError && (
          <div
            className="text-[11px] px-4 py-1 border-b"
            style={{
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              borderColor: 'var(--danger)',
            }}
          >
            {moveError}
          </div>
        )}
        <div
          className="flex-1 overflow-y-auto py-2"
          style={
            dropTargetId === 'root' ? { background: 'var(--accent-bg)' } : undefined
          }
          onDragOver={(e) => {
            e.preventDefault();
            setDropTargetId('root');
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) setDropTargetId(null);
          }}
          onDrop={handleDropOn(null)}
        >
          {list.isLoading && (
            <p className="text-xs text-[var(--text-dim)] px-4">{t('common.loading')}</p>
          )}
          {list.data && list.data.length === 0 && (
            <p className="text-xs text-[var(--text-dim)] px-4 py-4">{t('notes.empty')}</p>
          )}
          {usedTags.length > 0 && (
            <div
              className="px-3 pb-2 mb-2 border-b border-[var(--border)] flex flex-wrap gap-1"
              aria-label={t('notes.tags_filter')}
              data-testid="notes-tag-filter"
            >
              {usedTags.map((tag) => (
                <span key={tag.id} style={{ opacity: tagFilter && tagFilter !== tag.id ? 0.45 : 1 }}>
                  <TagPill
                    label={tag.name}
                    color={tag.color ?? 'neutral'}
                    size="sm"
                    onClick={() => setTagFilter(tagFilter === tag.id ? null : tag.id)}
                  />
                </span>
              ))}
              {tagFilter && (
                <button
                  type="button"
                  className="text-[10px] text-[var(--text-muted)] hover:underline ml-1"
                  onClick={() => setTagFilter(null)}
                >
                  {t('notes.tags_all')}
                </button>
              )}
            </div>
          )}
          {tagFilter && (
            <ul data-testid="notes-tag-results">
              {taggedNotes.length === 0 && (
                <li className="text-xs text-[var(--text-dim)] px-4 py-2">
                  {t('notes.tags_no_match')}
                </li>
              )}
              {taggedNotes.map((n) => (
                <li key={`tag-${n.id}`}>
                  <button
                    type="button"
                    onClick={() => selectNote(n.id)}
                    className="w-full text-left px-3 py-1 text-sm truncate transition-colors hover:bg-[var(--bg-hover)]"
                    style={
                      selectedId === n.id
                        ? { background: 'var(--accent-bg)', color: 'var(--accent)' }
                        : { color: 'var(--text-muted)' }
                    }
                  >
                    {n.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!tagFilter && pinnedNotes.length > 0 && (
            <div className="mb-2 pb-2 border-b border-[var(--border)]">
              <div
                className="px-3 py-1 text-[10px] uppercase font-semibold flex items-center gap-1.5 font-mono"
                style={{ color: 'var(--text-muted)', letterSpacing: '0.12em' }}
              >
                <StarIcon filled className="w-3 h-3" style={{ color: 'var(--c-amber)' }} />
                {t('notes.pinned_section')}
              </div>
              <ul>
                {pinnedNotes.map((n) => (
                  <li key={`pin-${n.id}`}>
                    <button
                      type="button"
                      onClick={() => selectNote(n.id)}
                      className="w-full text-left px-3 py-1 text-sm truncate transition-colors"
                      style={
                        selectedId === n.id
                          ? { background: 'var(--accent-bg)', color: 'var(--accent)' }
                          : { color: 'var(--text-muted)' }
                      }
                      onMouseEnter={(e) => {
                        if (selectedId !== n.id) {
                          e.currentTarget.style.background = 'var(--bg-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedId !== n.id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <StarIcon
                        filled
                        className="inline w-3 h-3 mr-1.5"
                        style={{ color: 'var(--c-amber)' }}
                      />
                      {n.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!tagFilter && tree.length > 0 && (
            <div
              className="px-3 py-1 text-[10px] uppercase font-semibold font-mono"
              style={{ color: 'var(--text-muted)', letterSpacing: '0.12em' }}
            >
              {t('notes.folders_section')}
            </div>
          )}
          <ul hidden={!!tagFilter}>
            {tree.map((node, idx) => (
              <TreeItem
                key={node.id}
                node={node}
                depth={0}
                color={NOTE_ROOT_COLORS[idx % NOTE_ROOT_COLORS.length]}
                selectedId={selectedId}
                dropTargetId={dropTargetId}
                draftIds={draftIds}
                onSelect={selectNote}
                onAddChild={(parentId) => {
                  setCreatingUnder(parentId);
                  setSelectedId(null);
                }}
                onRename={(id, title) => renameMutation.mutate({ id, title })}
                onTogglePin={(id, pinned) =>
                  pinMutation.mutate({ id, pin: !pinned })
                }
                onDragStartItem={(id, e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('application/cerbes-note', id);
                }}
                onDragOverItem={(id, e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  setDropTargetId(id);
                }}
                onDropItem={(id, e) => handleDropOn(id)(e)}
              />
            ))}
          </ul>
        </div>
      </aside>

      {templatePickerFor !== null && keyPair && (
        <TemplatePickerModal
          tenantId={tenantId}
          parentId={templatePickerFor === 'root' ? null : templatePickerFor}
          keyPair={keyPair}
          onClose={() => setTemplatePickerFor(null)}
          onCreated={(newId) => {
            setTemplatePickerFor(null);
            queryClient
              .invalidateQueries({ queryKey: ['notes', tenantId] })
              .then(() => selectNote(newId));
          }}
        />
      )}

      {/* Editor / preview în dreapta — full width restul ecranului.
          Primește .app-main-bg (light play) ca să match-uiască restul
          paginilor. */}
      <section
        className={`app-main-bg ${
          showDetailPanel
            ? isMobile
              ? 'flex flex-col flex-1 overflow-hidden'
              : 'flex-1 overflow-y-auto'
            : 'hidden'
        }`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {isMobile && showDetail && (
          <div
            className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] flex-shrink-0"
            style={{ background: 'var(--bg-elevated)' }}
          >
            <button
              type="button"
              onClick={() => {
                setSelectedId(null);
                setCreatingUnder(null);
                if (tenantSlug) navigate(`/notes/${tenantSlug}`, { replace: false });
                else navigate('/notes', { replace: false });
              }}
              className="flex items-center gap-1.5 text-sm rounded px-2 py-1"
              style={{ color: 'var(--text-muted)' }}
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span>{t('notes_ui.list.back_to_list')}</span>
            </button>
            <span
              className="ml-auto text-[10px] font-mono uppercase"
              style={{ color: 'var(--text-dim)', letterSpacing: '0.12em' }}
            >
              {t('notes_ui.list.swipe_hint')}
            </span>
          </div>
        )}
        <div
          className={`px-4 py-4 md:px-8 md:py-6 ${
            isMobile ? 'flex-1 overflow-y-auto' : ''
          }`}
        >
          {isCreating ? (
            <NoteEditor
              key={`new-${creatingUnder}`}
              tenantId={tenantId}
              noteId={null}
              parentId={creatingUnder === 'root' ? null : (creatingUnder as string)}
              onSaved={(note) => {
                setCreatingUnder(null);
                // Forțăm refresh list ca să avem slug-ul nou
                queryClient
                  .invalidateQueries({ queryKey: ['notes', tenantId] })
                  .then(() => selectNote(note.id));
              }}
              onCancel={() => setCreatingUnder(null)}
            />
          ) : selectedId ? (
            <NoteEditor
              key={selectedId}
              tenantId={tenantId}
              noteId={selectedId}
              parentId={null}
              onSaved={() => {}}
              onDeleted={() => {
                setSelectedId(null);
                navigate('/notes');
              }}
            />
          ) : (
            <div className="text-center text-sm text-[var(--text-muted)] pt-20">
              {t('notes.empty_select_hint')}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
